import '@sg-widgets/platform';
import { isDefined } from '@sgme/fp';
import type { SGWTConnectError } from '@sgwt/connect-core';
import { Action, EmptyObject, Store } from 'redux';
import { map } from 'rxjs/operators';
import { configureStore } from 'state/configureStore';
import { AppState } from 'state/state.model';
import type { IUserCurrent } from 'state/user';
import { userCurrentReceived, userInfoReceived } from 'state/user';
import { matomoStarting } from 'state/userConsent/userConsent.actions';
import { userPreferencesRequested } from 'state/userPreferences/userPreferences.action';
import { getImpersonatedUser, setFakePermissions, setImpersonatedUser } from 'utils/fakesso/userImpersonation';
import { httpClient } from 'utils/http/httpClient';
import { getFrontLogger } from 'utils/loggers/FrontLogger';
import { Streaming } from 'utils/streaming/model';
import { initStreaming } from 'utils/streaming/streaming';
import { getFakeSsoUserEmail, redirectToRequestAccessPage } from 'utils/url';

import '../app.scss';
import '../utils/scrollIntoViewIfNeededPolyfill';
import { sgwtConnect } from '../api/connect';
import { bootstrapReactApp } from './App/App.bootstrap';
import { bootstrap } from './bootstrap';
import { getEnvironment, getQuoteIdFromUrl, getRfqIdFromUrl } from './utils';
export interface UserAuthorization {
  resource: string;
  permissions: ReadonlyArray<{
    name: Permission;
  }>;
}

interface OpenIDClaims {
  user_authorization: readonly UserAuthorization[];
  contact_id: string;
}

const SGM_SP_RESOURCE = 'SG Markets Structured Products';

export function extractPermissions(
  user_authorization: readonly UserAuthorization[],
): readonly Permission[] | undefined {
  const permissionsNames = user_authorization
    ?.find(({ resource }) => resource === SGM_SP_RESOURCE)
    ?.permissions?.map(({ name }) => name);

  if (window.sgmeConfiguration?.enableFakeSso) {
    const fakeUser = getImpersonatedUser();
    if (isDefined(fakeUser)) {
      const fakePermissionsNames =
        window.sgmeConfiguration?.navigateAs?.find(user => user.name === fakeUser)?.permissions ??
        window.sgmeConfiguration?.defaultFakePermissions;
      setFakePermissions(fakePermissionsNames);
      return fakePermissionsNames;
    } else if (!isDefined(permissionsNames)) {
      const fakePermissionsNames = window.sgmeConfiguration?.defaultFakePermissions;
      setFakePermissions(fakePermissionsNames);
      return fakePermissionsNames;
    }
  }

  return permissionsNames;
}

function activateFakeSsoIfNeeded(): Record<string, string> {
  const params: Record<string, string> = {};
  const fakeUser = getFakeSsoUserEmail();
  if (fakeUser) {
    params['fakesso'] = fakeUser;
    setImpersonatedUser(fakeUser);
  }
  return params;
}

function callUserCurrent(
  store: Store<EmptyObject & AppState, Action<any>> & { dispatch: unknown },
  params: Record<string, string>,
  environment: string,
  streaming: Streaming,
) {
  httpClient
    .getJSON<IUserCurrent>({ url: 'api/v1/users/current', params })
    .pipe(map(x => x.payload))
    .subscribe({
      next: user => {
        if (user.isInternal) {
          try {
            const newScript = document.createElement('script');
            newScript.src = window.sgmeConfiguration.cdnIcWidgets + '/v1/ic-contact-multi-picker.js';
            newScript.defer = true;
            document.getElementById('scriptsRoot')?.appendChild(newScript);
          } catch (e) {
            getFrontLogger().warn('Unable to load IC widget for internal user {error}', e);
          }
        }
        store.dispatch(userCurrentReceived(user));
        store.dispatch(userPreferencesRequested());

        const piwikAnalytics = user.environmentDetails?.piwikAnalytics;
        if (piwikAnalytics?.piwikBaseUrl !== undefined && piwikAnalytics?.piwikSiteId !== undefined) {
          store.dispatch(
            matomoStarting(
              piwikAnalytics.piwikBaseUrl,
              piwikAnalytics.piwikSiteId,
              user.login,
              user.isInternal,
              user.companyName,
            ),
          );
        }
        bootstrapReactApp('root', store, streaming, environment);

        const { connectionEvent$ } = streaming;
        connectionEvent$.subscribe(({ event, ...others }) => {
          store.dispatch({ type: 'SIGNALR_' + event.toUpperCase(), ...others });
        });
      },
      error() {
        document.location.href = `https://shared.sgmarkets.com/outdated_browsers/service-maintenance.html?application=SGMarkets%20Structured%20Products&redirectUri=${window.location.href}`;
      },
    });
}

function bootstrapApplication(environment: string) {
  const params = activateFakeSsoIfNeeded();
  const streaming = initStreaming();
  const store = configureStore(streaming);

  if (window.sgmeConfiguration?.disableSgConnect) {
    // for integration test only
    callUserCurrent(store, params, environment, streaming);
  } else {
    window.SGWTWidgetConfiguration.bus.subscribe<OpenIDClaims>('sg-connect.user-info', claims => {
      const logger = getFrontLogger();
      if (!isDefined(claims)) {
        logger.info('SG Connect claims not defined');
        return;
      }
      const { user_authorization, contact_id } = claims;
      const permissionsNames = extractPermissions(user_authorization);
      if (!isDefined(permissionsNames)) {
        redirectToRequestAccessPage();
      } else {
        logger.info('SG Connect {permissionsNames}', JSON.stringify(permissionsNames));
        store.dispatch(userInfoReceived(permissionsNames, contact_id));
        callUserCurrent(store, params, environment, streaming);
      }
    });

    sgwtConnect.on('renewAuthorizationError', (error: SGWTConnectError) => {
      const frontLogger = getFrontLogger();
      frontLogger.info('Failed to renew the SG Connect token {@FrontError}', error);
    });
    sgwtConnect.on('authorizationExpired', () => {
      const frontLogger = getFrontLogger();
      frontLogger.info('Token is no longer valid.');
    });
  }
}

// Run application once the Sg-connect authentication has succeeded
export function onSgConnectAuthenticated() {
  const environment = getEnvironment(window.sgmeConfiguration) ?? 'PROD';
  const quoteId = getQuoteIdFromUrl(window.location.hash);
  const rfqId = getRfqIdFromUrl(window.location.hash);

  bootstrap(environment, quoteId, rfqId, bootstrapApplication, closeTab, openQuote, openRfq);
}

function openQuote(quoteId: string) {
  document.location.hash = `/quote/${quoteId}`;
}

function openRfq(rfqId: string) {
  document.location.hash = `/sharable-quote/${rfqId}`;
}

function closeTab() {
  window.top?.close();
  document.location.href = 'app_closed.static.html';
}
